import React, { useContext } from "react";
import BreakpointContext from "../../contexts/breakpointContext";
import useDraftPicks from "../../hooks/queryResults/draft/useDraftPicks";
import useDraftSettings from "../../hooks/queryResults/draft/useDraftSettings";
import useNflTeams from "../../hooks/queryResults/nfl/useNflTeams";
import useAllPlayers from "../../hooks/queryResults/useAllPlayers";
import useIsLoading from "../../hooks/queryResults/useIsLoading";
import useTeams from "../../hooks/queryResults/useTeams";
import NflUtil from "../../util/nflUtil";
import Util from "../../util/util";
import PlayerImageByPos from "../player/PlayerImageByPos";
import {
  getPlayerInjuryCross,
  getPlayerName,
  getPlayerNameFull,
} from "../player/PlayerName";
import Loading from "../ui/Loading";
import styled from "styled-components";
import TeamLogoWithRosterLink from "./TeamLogoWithRosterLink";

const StyledContainerDiv = styled.div``;

const StyledTable = styled.table`
  margin: 0 auto;

  & td,
  & th {
    padding: 0.25rem 0.5rem;
    height: 47px;
  }
`;

export default function Picks() {
  const breakpoints = useContext(BreakpointContext);
  const { draftPicksData, draftPicksQuery } = useDraftPicks();
  const { draftSettingsData, draftSettingsQuery } = useDraftSettings();
  const { playersData, playersQuery } = useAllPlayers();
  const { teams, teamsQuery } = useTeams();
  const { nflTeamsData, nflTeamsQuery } = useNflTeams();

  const isLoading = useIsLoading(
    draftPicksData,
    draftPicksQuery,
    draftSettingsData,
    draftSettingsQuery,
    playersData,
    playersQuery,
    teams,
    teamsQuery,
    nflTeamsData,
    nflTeamsQuery
  );

  const headerRow = (key) => (
    <tr key={key}>
      {breakpoints.sm ? (
        <>
          <th>Round</th>
          <th>Pick</th>
          <th>Team</th>
          <th>&nbsp;</th>
          <th>Pos</th>
          <th>Player</th>
          <th>NFL</th>
          <th>Bye</th>
        </>
      ) : (
        <>
          <th>Pk</th>
          <th>Tm</th>
          <th>Player</th>
        </>
      )}
    </tr>
  );

  return isLoading ? (
    <Loading />
  ) : (
    <StyledContainerDiv>
      <StyledTable>
        <thead>{headerRow("header")}</thead>
        <tbody>
          {draftPicksData.picks.map((pick, i) => {
            let teamLogo = null;
            if (pick.team) {
              const team = teams.getTeamById(pick.team);
              teamLogo = <TeamLogoWithRosterLink team={team} size={"sm"} />;
            }

            let player = null;
            let playerName = null;
            let playerImage = null;
            let pos = null;
            let nflTeamAbbr = null;
            let byeWeek = null;

            if (pick.player) {
              player = playersData.players[pick.player];

              pos = player.pos;
              playerName = getPlayerNameFull(player);
              playerImage = (
                <PlayerImageByPos player={player} isAnimated={false} />
              );

              if (player.nflTeamAbbr) {
                nflTeamAbbr = NflUtil.formatNflTeamAbbr(player.nflTeamAbbr);
                const nflTeam = nflTeamsData.nflTeams[player.nflTeamAbbr];
                if (nflTeam) byeWeek = nflTeam.byeWeek;
              }
            }

            const showHeaderRow =
              pick.pickNum % draftSettingsData.settings.numTeams === 0 &&
              pick.pickNum <
                draftSettingsData.settings.numTeams *
                  draftSettingsData.settings.numRounds;

            const round = Util.getRoundFromPickNum(
              pick.pickNum,
              draftSettingsData.settings.numTeams
            );

            const injury = getPlayerInjuryCross(player);

            return (
              <React.Fragment key={"fragment" + i}>
                <tr>
                  {breakpoints.sm ? (
                    <>
                      <td>{round}</td>
                      <td>{pick.pickNum}</td>
                      <td>{teamLogo}</td>
                      <td>{playerImage}</td>
                      <td>{pos}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left" }}>
                        {playerName}
                        {injury}
                      </td>
                      <td>{nflTeamAbbr}</td>
                      <td>{byeWeek}</td>
                    </>
                  ) : (
                    <>
                      <td>
                        {round}-{pick.pickNum}
                      </td>
                      <td>{teamLogo}</td>
                      <td className={"left"}>
                        {getPlayerName(player, {
                          pos: 1,
                          short: 1,
                          nfl: 1,
                          bye: byeWeek,
                          injury: 1,
                        })}
                      </td>
                    </>
                  )}
                </tr>
                {showHeaderRow ? headerRow("header" + i) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </StyledTable>
    </StyledContainerDiv>
  );
}
